import type { ReactNode } from 'react';
import { BookmarkIcon, FolderIcon, HomeIcon, SearchIcon, SettingsIcon, TvIcon } from '../Icon.tsx';
import type { Route } from '../route.ts';

interface NavTab {
  key: string;
  label: string;
  icon: ReactNode;
  active: boolean;
  onClick: () => void;
}

/**
 * Mobil alt gezinme — V5-10 "ölçeklenebilir" model.
 *
 * Eski model altı tür sekmesi taşıyordu (Ana Sayfa, Filmler, Diziler,
 * Müzikler, Kütüphaneler, Hesap) ve 360 px'de etiketler 11 px'e düşüyordu.
 * Yeni model beş hedefe indirir:
 *
 *   Ana Sayfa · Ara · Kütüphane · Listem · Hesap
 *
 * Film/dizi/müzik ayrımı kütüphane ekranının içinde kalır; böylece kullanıcı
 * farklı adlarda çok sayıda kütüphane eklediğinde alt bar büyümez. "Listem"
 * sürekli erişilebilir hâle gelir.
 *
 * ## "TV" sekmesi
 *
 * Canlı TV altıncı sekme olarak eklendi. Etiket kısa tutuldu ("TV" — "Canlı
 * TV" 360 px'de alt barı sıkıştırırdı); ekran adı erişilebilirlik için tam
 * yazılıyor. Masaüstü/TV tarafında aynı hedef **kenar çubuğunda** duruyor.
 */
export function BottomNav({
  currentRoute,
  onNavigate,
}: {
  currentRoute: Route;
  onNavigate: (route: Route) => void;
}): ReactNode {
  const isHome = currentRoute.name === 'home';
  const isSearch = currentRoute.name === 'search';
  const isLibrary =
    currentRoute.name === 'kutuphaneler' ||
    currentRoute.name === 'library' ||
    currentRoute.name === 'filmler' ||
    currentRoute.name === 'diziler' ||
    currentRoute.name === 'muzik' ||
    currentRoute.name === 'muzik-album' ||
    currentRoute.name === 'muzik-artist';
  const isWatchlist = currentRoute.name === 'listem';
  const isAccount = currentRoute.name === 'hesap';
  const isLiveTv = currentRoute.name === 'canli-tv';

  const tabs: NavTab[] = [
    {
      key: 'home',
      label: 'Ana Sayfa',
      icon: <HomeIcon size={20} />,
      active: isHome,
      onClick: () => onNavigate({ name: 'home' }),
    },
    {
      key: 'search',
      label: 'Ara',
      icon: <SearchIcon size={20} />,
      active: isSearch,
      onClick: () => onNavigate({ name: 'search', query: '' }),
    },
    {
      key: 'library',
      label: 'Kütüphane',
      icon: <FolderIcon size={20} />,
      active: isLibrary,
      onClick: () => onNavigate({ name: 'kutuphaneler' }),
    },
    {
      key: 'live-tv',
      label: 'TV',
      icon: <TvIcon size={20} />,
      active: isLiveTv,
      onClick: () => onNavigate({ name: 'canli-tv' }),
    },
    {
      key: 'listem',
      label: 'Listem',
      icon: <BookmarkIcon size={20} />,
      active: isWatchlist,
      onClick: () => onNavigate({ name: 'listem' }),
    },
    {
      key: 'account',
      label: 'Hesap',
      icon: <SettingsIcon size={20} />,
      active: isAccount,
      onClick: () => onNavigate({ name: 'hesap' }),
    },
  ];

  return (
    <nav className="bottom-nav" aria-label="Mobil Gezinme">
      {tabs.map((tab) => (
        <button
          key={tab.key}
          type="button"
          className={`bottom-nav__item ${tab.active ? 'is-active' : ''}`}
          aria-current={tab.active ? 'page' : undefined}
          title={tab.key === 'live-tv' ? 'Canlı TV' : undefined}
          onClick={tab.onClick}
        >
          <span className="bottom-nav__icon" aria-hidden="true">
            {tab.icon}
          </span>
          <span className="bottom-nav__label">{tab.label}</span>
        </button>
      ))}
    </nav>
  );
}
